import type { InboxConversationQuery } from "@qw/graphql";
import { cn } from "@qw/ui";
import { useLocation } from "react-router";
import { useInboxRail } from "@/inbox/use-inbox-rail";
import type { InboxInsured } from "@/providers/use-inbox-insured";
import { LinkInsuredButton } from "./components/link-insured-button";
import { OpenInsuredInBrokerageButton } from "./components/open-insured-in-brokerage-button";
import { UnlinkInsuredButton } from "./components/unlink-insured-button";
import { ViewLinkedEmailButton } from "./components/view-linked-email-button";
import { InsuredHoverCard } from "./insured-hover-card";

interface MiniAppActionsProps {
  className?: string;
  inboxConversation: InboxConversationQuery["inboxConversation"] | null | undefined;
  insured: InboxInsured | null | undefined;
  insuredId: string | null | undefined;
  rootPath: string;
  showInsuredHoverCard?: boolean;
}

interface InsuredScopeTargetOptions {
  insuredId: string | null | undefined;
  pathname: string;
  rootPath: string;
  search: string;
}

/**
 * Query state that describes the list the broker is leaving rather than the one
 * `ViewLinkedEmailButton` asks for, so none of it survives the move:
 *
 * - `term` filters a list whose control promises "all", and a committed term also
 *   outranks the insured selection, sending the rail back to the Book.
 * - `unlinked` does not merely narrow — it *wins*. Both the Prisma and raw-SQL
 *   paths read `if (unlinked) … else if (insuredId)`, so `insuredId` is dropped
 *   outright and the list comes back unlinked under an insured chip.
 * - `subject` and `from` are the search box's operator chips, and they narrow
 *   the list for the same reason `term` does — so "all linked email" would come
 *   back scoped to one subject, with only a chip inside the box to say so.
 * - `page` is an offset into a result set that no longer exists.
 *
 * Everything else rides along, `conversationId` above all: scoping is a narrowing
 * of what is already on screen, so the open email stays open.
 */
const SCOPE_DROPPED_SEARCH_PARAMS = ["page", "term", "subject", "from", "unlinked"];

/**
 * Where "all of this insured's linked email" lives, or `null` if the broker is
 * already looking at it.
 *
 * The route is the chip: `useClearScopeTarget` in `app/search-scope-chips.tsx`
 * derives the box's insured scope from `:insuredId` in the path. But being on
 * that path is not the same as seeing all of it — `/inbox/:insuredId?term=x` is
 * the insured's mail *filtered*, and clearing the filter is exactly what this
 * control is for. So it only disappears when both halves already hold: the path
 * carries the insured, and none of the params that narrow the list are set.
 *
 * Already under the insured, the path is kept as-is rather than reset to the
 * bare insured route. A `/files` suffix is the rail's tab, not a filter on the
 * mail list, and moving the broker's tab is not this control's business.
 */
const getInsuredScopeTarget = ({ insuredId, pathname, rootPath, search }: InsuredScopeTargetOptions) => {
  if (!insuredId) {
    return null;
  }

  const insuredPathname = `${rootPath}/${insuredId}`;
  const isUnderInsured = pathname === insuredPathname || pathname.startsWith(`${insuredPathname}/`);
  const scopedSearch = new URLSearchParams(search);
  const droppedParam = SCOPE_DROPPED_SEARCH_PARAMS.some((param) => scopedSearch.has(param));

  for (const param of SCOPE_DROPPED_SEARCH_PARAMS) {
    scopedSearch.delete(param);
  }

  if (isUnderInsured && !droppedParam) {
    return null;
  }

  return { pathname: isUnderInsured ? pathname : insuredPathname, search: scopedSearch.toString() };
};

/**
 * shadcn's button-group recipe: outline segments with their touching borders and
 * radii collapsed, so four buttons read as one control. Deliberately not
 * `@qw/ui`'s `ButtonGroup`, which is an action-bar layout (`gap-2`,
 * `justify-end`) rather than a segmented group — promoting this into the library
 * under its own name is worth its own ticket.
 *
 * `focus-visible:relative z-10` is load-bearing rather than decoration: with the
 * left borders removed, a focus ring on a middle segment is clipped by its
 * neighbour until the focused segment is raised out of flow.
 *
 * The segments keep `outline`'s border and drop its `bg-popover` fill, so the
 * group is an outline on the header rather than a raised block. Hover still
 * reads: `outline` darkens the border, and the icon goes to full contrast.
 */
const BUTTON_GROUP_CLASS_NAME = [
  "flex w-fit items-center",
  "[&>*]:bg-transparent",
  "[&>*]:focus-visible:relative [&>*]:focus-visible:z-10",
  "[&>*:not(:first-child)]:rounded-l-none [&>*:not(:first-child)]:border-l-0",
  "[&>*:not(:last-child)]:rounded-r-none",
].join(" ");

/**
 * Everything the broker can do to the insured in the rail, beside the
 * Active/Files tabs.
 *
 * The header row above holds the name alone, because at the Book panel's 220px
 * drag floor a name and four controls on one line leaves the name a few
 * characters. Sharing the tab row buys back the whole width.
 *
 * `LinkInsuredButton` sits outside the group on purpose — it is a wide labelled
 * dropdown rather than an icon, and it only appears when there is no insured, so
 * the group it would join is empty anyway.
 */
export const MiniAppActions = ({
  className,
  inboxConversation,
  insured,
  insuredId,
  rootPath,
  showInsuredHoverCard = true,
}: MiniAppActionsProps) => {
  // Merged mode moves linking beside the star, where it sits next to the email it acts on instead
  // of in the rail. Two copies would be two chances to disagree about which email is open.
  const { isMergedRail } = useInboxRail();
  const { pathname, search } = useLocation();
  const scopeTarget = getInsuredScopeTarget({ insuredId, pathname, rootPath, search });
  const canLink = !isMergedRail && Boolean(inboxConversation);
  const hasGroupedControl = Boolean((insured && showInsuredHoverCard) || scopeTarget || insuredId);

  if (!hasGroupedControl && !canLink) {
    return null;
  }

  return (
    <div className={cn("flex flex-none items-center gap-2", className)}>
      {hasGroupedControl && (
        <div role="group" aria-label="Insured actions" className={BUTTON_GROUP_CLASS_NAME}>
          {insured && <InsuredHoverCard enabled={showInsuredHoverCard} insured={insured} />}
          {scopeTarget && <ViewLinkedEmailButton to={scopeTarget} />}
          {insuredId && <OpenInsuredInBrokerageButton insuredId={insuredId} />}
          {canLink && insuredId && inboxConversation && (
            <UnlinkInsuredButton conversationId={inboxConversation.id} rootPath={rootPath} variant="outline" />
          )}
        </div>
      )}
      {canLink && !insuredId && inboxConversation && <LinkInsuredButton conversationId={inboxConversation.id} />}
    </div>
  );
};
